"use client";

import { ContentNavEvents, trackEvent } from "@/analytics";

import { Button } from "@/shadcn/ui/button";
import { ShareIcon as LucideShareIcon } from "lucide-react";
import ShareIcon from "./icons/ShareIcon";
import {
  ShareModal,
  type ShareDataWithUrl,
} from "@punchup/ui-common/components/common";
import { cn } from "./utils";
import { CSSProperties, useState } from "react";

export type { ShareDataWithUrl };

interface Props {
  className?: string;
  shareData: ShareDataWithUrl;
  location?: string;
  label?: string;
  iconColor?: string;
  iconWidth?: number;
  iconHeight?: number;
  classicButton?: boolean;
  gap?: number;
  style?: CSSProperties;
}

/**
 * A component for sharing a URL via the Web Share API.
 * In the case that the user's browser doesn't support the Web Share API
 * a modal with standard sharing options will open instead.
 */
export default function ShareButton({
  className,
  shareData,
  location,
  label,
  iconColor = "stroke-primary-blue",
  iconWidth = 24,
  iconHeight = 24,
  classicButton = false,
  gap,
  style,
}: Props) {
  const [showModal, setShowModal] = useState<boolean>(false);

  let shareApiSupported = false;
  let shareableData: ShareData | null = null;

  // if this browser supports the `canShare` method
  if (Boolean(navigator.canShare)) {
    // ensure our payload is shareable
    shareableData = shareData;
    shareApiSupported = navigator.canShare(shareData);

    // if the payload with `text` is not supported, try removing it
    if (!shareApiSupported) {
      const shareDataWithoutText = { ...shareData };
      delete shareDataWithoutText.text;

      shareableData = shareDataWithoutText;
      shareApiSupported = navigator.canShare(shareDataWithoutText);
    }
  }

  const handleShareClick = () => {
    const variant =
      shareApiSupported && shareableData ? "webShareApi" : "modal";

    trackEvent(ContentNavEvents.CLICKED_SHARE, {
      variant,
      location,
    });

    if (shareApiSupported && shareableData) {
      navigator
        .share(shareableData)
        .then((e) => {
          trackEvent(ContentNavEvents.WEB_SHARE_SUCCEEDED, {
            variant,
            location,
            success: true,
          });
        })
        .catch((error) => {
          trackEvent(ContentNavEvents.WEB_SHARE_FAILED, {
            variant,
            location,
            error: error,
          });
        });
    } else {
      setShowModal(true);
    }
  };

  return (
    <>
      {classicButton ? (
        <Button
          variant="punchup"
          onClick={handleShareClick}
          className={className}
          style={style}
        >
          <LucideShareIcon className="w-4 h-4 mr-2" />
          {label}
        </Button>
      ) : (
        <button
          type="button"
          title="share"
          className={cn(
            "p-2 transition-colors duration-200 ease-in-out hover:bg-gray-50 border border-transparent hover:border-slate-200 active:border-slate-300 rounded-md text-[13px] md:text-[15px]",
            "text-primary-blue stroke-primary-blue",
            className
          )}
          onClick={handleShareClick}
          style={style}
        >
          <div
            className={cn(
              "flex flex-row justify-center items-center",
              !gap && "gap-x-1.5 md:gap-x-2"
            )}
            style={{
              gap,
            }}
          >
            <ShareIcon
              className={cn(
                "transition-colors duration-200 ease-in-out stroke-inherit",
                iconColor
              )}
              width={iconWidth}
              height={iconHeight}
            />
            {label && (
              <span className="font-semibold leading-none text-inherit">
                {label}
              </span>
            )}
          </div>
        </button>
      )}

      <ShareModal
        open={showModal}
        setOpen={setShowModal}
        shareData={shareData}
      />
    </>
  );
}
